import OrdersTable from "@/dashboard/my-orders/orders-table/orders-table.vue";
import { routerModel } from "@/router/router.model";
import {
    APIResponse,
    EnumUserRole,
    GetEnumSortDataDto,
    OrderDto,
    OrderPaymentDetailsDto,
    OrdersTabsType,
    OrderStatisticsDto,
    OrderStatus,
    OrderStatusStatisticDto,
    SortConfigurationDto,
    SortOptionDto,
} from "@/shared/api-contract/api-contract";
import { AppPaginationPerPage } from "@/shared/components/app-pagination/app-pagination";
import { pageLoaderModel } from "@/shared/models/page-loader";
import { computed, watch } from "vue";
import { Options, setup, Vue } from "vue-class-component";
import DashboardSidebar from "../dashboard-sidebar/dashboard-sidebar.vue";
import "./my-orders.scss";
import { appAxios } from "@/core/utils/appAxios";
import { eff } from "@/core/utils/effect-ts-abbr";
import { dashboardModel } from "../dashboard.model";
import OrdersTabs from "@/dashboard/my-orders/orders-tabs/orders-tabs.vue";
import { currentUserModel } from "@/shared/state/current-user.model";
import { appCompanyStatusDialogModel } from "@/shared/components/app-company-status-dialog/app-company-status-dialog.model";
import DeclineDialog from "@/dashboard/my-orders/new-popup/decline-dialog/decline-dialog.vue";
import OrderApprovePopup from "@/dashboard/my-orders/new-popup/order-approve-popup/order-approve-popup.vue";
import PaymentsTransactionsPopup from "@/dashboard/my-orders/new-popup/payments-transactions-popup/payments-transactions-popup.vue";
import ProformaPopup from "@/dashboard/my-orders/new-popup/proforma-popup/proforma-popup.vue";
import { defaultPaginationQueryParameters } from "@/shared/vehicles/vehicles.model";
import SortBlockComponent from "@/shared/vehicles/search-bar/sort-block/sort-block.vue";
import { useIsTablet } from "@/shared/composables/use-is-tablet";
import { isEmpty } from "lodash";
import { notEqualsStates } from "@/shared/utils";

const initialDashboardData: OrderStatusStatisticDto[] = [];

@Options({
    components: {
        "dashboard-sidebar": DashboardSidebar,
        "orders-table": OrdersTable,
        "orders-tabs": OrdersTabs,
        DeclineDialog,
        OrderApprovePopup,
        PaymentsTransactionsPopup,
        ProformaPopup,
        SortBlock: SortBlockComponent,
    },
})
export class MyOrders extends Vue {
    pageLoader = pageLoaderModel;
    orderStatusEnum = OrderStatus;

    currentPage = 1;
    perPage = AppPaginationPerPage._12;
    total = 0;
    intervalId: any;
    orders: readonly OrderDto[] = [];
    selectedOrders: Array<OrderDto> = [];

    currentOrderStatus: OrderStatus = OrderStatus.to_approve;
    tabsInfo: OrderStatusStatisticDto[] = initialDashboardData;
    tabsStats: OrdersTabsType = {} as OrdersTabsType;

    dateModel: Date[] = [];
    searchKeyword = "";

    isLoading = false;
    isLoadingOrders = false;
    isLoadingTabsStat = false;
    showConfirm = false;
    showDecline = false;
    showPay = false;
    updateData = false;

    declineOrderIds: number[] = [];
    approveOrders: OrderDto[] = [];
    pfnrOrderIds: string[] = [];
    isPaymentsTransactionsPopup = false;
    orderId = 0;
    orderPaymentDto: OrderPaymentDetailsDto | null = null;
    viewProformaOrder: OrderDto | null = null;
    isTabletCtx = setup(useIsTablet);
    sortModel: SortConfigurationDto = {
        id_direction: 2,
        id_field: 3,
    };
    sortOptions = [] as readonly SortOptionDto[];
    openApprove() {
        this.approveOrders = eff.fn.pipe(this.selectedOrders, eff.array.toMutable);
    }
    closeDecline() {
        this.declineOrderIds = [];
    }
    closeProformaView() {
        this.viewProformaOrder = null;
    }
    openDecline() {
        this.declineOrderIds = eff.fn.pipe(this.selectedOrderIds, eff.array.toMutable);
    }
    totalPrice = computed<number>(() =>
        eff.fn.pipe(
            this.selectedOrders,
            eff.array.reduce(0, (acc, it) => acc + (it?.total_amount_euro_excl_vat || 0)),
        ),
    );

    closePaymentsTransactionsPopup(event) {
        const isCancelButton = event.target.closest(".header");
        const isProceedButton = event.target.closest(".button-proceed");
        if (isCancelButton || isProceedButton) {
            this.isPaymentsTransactionsPopup = false;
        }
    }

    get canShowPage() {
        return dashboardModel.canShowOrderHistoryPage.value;
    }

    beforeMount() {
        this.isLoading = true;
        this.pageLoader.toggle(true);
    }

    mounted() {
        this.fetchSortConfigurations();
        watch(
            () => ({
                currentRoute: routerModel.router.currentRoute.value,
                updateData: this.updateData,
                params: {
                    page: this.currentPage,
                    per_page: this.perPage,
                    status: OrderStatus[this.currentOrderStatus],
                    sort_field: this.sortModel.id_field,
                    sort_direction: this.sortModel.id_direction,
                },
            }),
            async ({ currentRoute, params }) => {
                this.isLoading = true;
                const { tab } = currentRoute.params;
                this.currentOrderStatus = (tab as OrderStatus) || OrderStatus.to_approve;
                params.status =
                    typeof tab === "string" ? OrderStatus[tab] : OrderStatus.to_approve;
                params.sort_direction = this.sortModel.id_direction;
                params.sort_field = this.sortModel.id_field;

                this.selectedOrders = [];
                await this.fetchOrders(params);
                await this.fetchOrdersTabsInfo();
                this.isLoading = false;
            },
            { immediate: true },
        );

        this.intervalId = setInterval(() => {
            this.updateOrders();
        }, 5000);
    }

    beforeUnmount() {
        clearInterval(this.intervalId);
    }

    /* API Connectivity --------------------------------------------------------------------------------------------- */
    fetchOrdersTabsInfo = async () => {
        if (this.isLoadingTabsStat) return;
        this.isLoadingTabsStat = true;
        appAxios.value
            .get<APIResponse<OrderStatisticsDto>>(`/client/web/orders/statistics`)
            .then((res) => {
                if (res?.data?.data) {
                    this.tabsInfo = res.data?.data.stats_per_status;
                    const newStats = {} as OrdersTabsType;
                    for (const elem in OrderStatus) {
                        newStats[elem] = res.data?.data.stats_per_status.find(
                            (el) => el.status.id.toString() === OrderStatus[elem],
                        );
                    }
                    this.tabsStats = {
                        ...newStats,
                        all: res.data?.data.total_orders_counter,
                    };
                }
            })
            .catch((res) => console.log("fetchOrdersTabsInfo error", res))
            .finally(() => {
                this.isLoadingTabsStat = false;
            });
    };

    async fetchOrders(params: Record<string, unknown>) {
        if (this.isLoadingOrders || params?.status === undefined) return;
        this.isLoadingOrders = true;

        try {
            const res = await appAxios.value
                .get<APIResponse<OrderDto[]>>(`/client/web/orders`, { params })
                .then((res) => {
                    if (!res?.data?.data) return;
                    this.orders = res.data.data;
                })
                .catch((resp) => {
                    this.total = 0;
                    this.orders = [];
                    console.log("Fetch Orders error:", resp);
                })
                .finally(() => {
                    this.pageLoader.toggle(false);
                    this.isLoadingOrders = false;
                });
        } catch (e) {
            this.total = 0;
            this.orders = [];
        }
    }

    async fetchSortConfigurations() {
        await appAxios.value
            .get<APIResponse<GetEnumSortDataDto>>(
                `/client/web/enumerator/order/sort-config`,
            )
            .then((response) => {
                if (response.data.status.code === 200) {
                    this.sortOptions = response.data.data?.available_options;
                    if (!isEmpty(response.data.data.default_option?.configuration)) {
                        this.sortModel = response.data.data.default_option?.configuration;
                        defaultPaginationQueryParameters.sort_field =
                            response.data.data.default_option?.configuration?.id_field;
                        defaultPaginationQueryParameters.sort_direction =
                            response.data.data.default_option?.configuration?.id_direction;
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    updateOrders() {
        appAxios.value
            .get<APIResponse<OrderStatisticsDto>>(`/client/web/orders/statistics`)
            .then((res) => {
                if (res?.data?.data) {
                    const { data } = res.data;
                    const needUpdateOrders = notEqualsStates(
                        data.stats_per_status,
                        this.tabsInfo,
                    );

                    if (needUpdateOrders) {
                        this.tabsInfo = data.stats_per_status;
                        const newStats = {} as OrdersTabsType;
                        for (const elem in OrderStatus) {
                            newStats[elem] = data.stats_per_status.find(
                                (el) => el.status.id.toString() === OrderStatus[elem],
                            );
                        }
                        this.tabsStats = {
                            ...newStats,
                            all: data.total_orders_counter,
                        };
                        const params = {
                            page: this.currentPage,
                            per_page: this.perPage,
                            status: OrderStatus[this.currentOrderStatus],
                            sort_field: this.sortModel.id_field,
                            sort_direction: this.sortModel.id_direction,
                        };
                        this.fetchOrders(params);
                    }
                }
            })
            .catch((res) => console.log("fetchOrdersTabsInfo error", res));
    }

    get selectedOrderIds() {
        return eff.fn.pipe(
            this.selectedOrders,
            eff.array.map((item) => +item.id.toString()),
        );
    }

    handlePay() {
        this.showPay = true;
    }

    get canApprove() {
        return (
            currentUserModel.userDto.value !== undefined &&
            currentUserModel.userDto.value?.role.id >= EnumUserRole.PURCHASOR
        );
    }

    get canDecline() {
        return this.canApprove;
    }

    showCompanyStatusDialog(
        type: "default" | "order-approve" | "order-decline" = "default",
    ): boolean {
        return appCompanyStatusDialogModel.show(type);
    }
}
